import { Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { FaEnvelope, FaLock, FaUserShield } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import BranchLicenseModal from '../Modals/BranchLicenseModal';
import TrialExpiredModal from '../Modals/TrialExpiredModal';

const BASE_URL = process.env.REACT_APP_API_URL;

function LoginTabs() {
  const [formData, setFormData] = useState({
    emailAddress: '',
    password: '',
    role: 'admin'
  });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const roleOptions = [
    { value: 'admin', label: 'Admin', endpoint: '/login' },
    { value: 'auditor', label: 'Auditor', endpoint: '/auditor-login' },
    { value: 'duser', label: 'Department User', endpoint: '/department-user-login' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.emailAddress) newErrors.emailAddress = 'Email address is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.role) newErrors.role = 'Please select a role';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const getEndpoint = () => {
    const selectedRole = roleOptions.find(role => role.value === formData.role);
    return selectedRole ? selectedRole.endpoint : '/login';
  };

  const getForgotPasswordLink = () => {
    const roleMap = {
      admin: '/forgot-password/admin',
      auditor: '/forgot-password/auditor',
      duser: '/forgot-password/dUser',
    };
    return roleMap[formData.role] || '/forgot-password/admin';
  };

  const handleRoleBasedNavigation = (userData, role) => {
    setTimeout(() => {
      switch (role) {
        case 'admin':
          if (userData.role === "Branch admin" && userData.accountType === "individual") {
            navigate("/IndividualDashboard");
          } else if (userData.role === "Company admin" && userData.accountType === "business") {
            navigate("/BusinessDashboard");
          }
          else if (userData.role === "Factory admin" && userData.accountType === "factory") {
            navigate("/FactoryDashboard");
          }
          else if (userData.role === "Branch admin" && userData.accountType === "Joined Branch") {
            navigate("/JBranchDashboard");
          }
          break;
        case 'auditor':
          if (userData.role === "Branch auditor" && userData.accountType === "individual") {
            navigate("/InAuditorDashboard");
          } else if (userData.role === "Company auditor" && userData.accountType === "business") {
            navigate("/BusinessAuditorDashboard");
          } 
          else if (userData.role === "Factory auditor" && userData.accountType === "factory") {
            navigate("/FactoryAuditorDashboard");
          } 
          else if (userData.role === "Branch auditor" && userData.accountType === "Joined Branch") {
            navigate("/JBranchAuditorDashboard");
          } 
          break;
        case 'duser':
          if (userData.role === "Department user" && userData.accountType === "individual") {
            navigate("/IndUserDashboard");
          } else if (userData.role === "Department user" && userData.accountType === "business") {
            navigate("/BusinessdUserDashboard");
          } 
          else if (userData.role === "Department user" && userData.accountType === "factory") {
            navigate("/FactorydUserDashboard");
          } 
          else if (userData.role === "Department user" && userData.accountType === "Joined Branch") {
            navigate("/JBranchdUserDashboard");
          } 
          break;
        default:
          navigate("/");
      }
    }, 1500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    
    if (!validateForm()) return;
    setLoading(true);

    try {
      const endpoint = getEndpoint();
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailAddress: formData.emailAddress,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message === "Trial period expired.") {
          setShowTrialModal(true);
          return;
        }
        if (data.message === "Branch license has expired." || data.message === "Company license has expired.") {
          setShowLicenseModal(true);
          return;
        }
        throw new Error(data.message || "Login failed");
      }

      let userData;
      let storageKey;

      // Handle different response structures based on role
      switch (formData.role) {
        case 'admin':
          userData = {
            id: data.user.id,
            firstName: data.user.firstName,
            lastName: data.user.lastName,
            email: data.user.emailAddress,
            role: data.user.role,
            plan: data.user.plan,
            company: data.user.company,
            factory: data.user.factory,
            CompanyCode: data.user.CompanyCode,
            BranchCode: data.user.BranchCode,
            FactoryCode: data.user.FactoryCode,
            accountType: data.user.accountType,
            trialRemainingDays: data.user.trialRemainingDays,
          };
          storageKey = 'user';
          break;
        case 'auditor':
          userData = {
            id: data.auditor.id,
            firstName: data.auditor.firstName,
            lastName: data.auditor.lastName,
            email: data.auditor.emailAddress,
            role: data.auditor.role,
            CompanyCode: data.auditor.CompanyCode,
            BranchCode: data.auditor.BranchCode,
            FactoryCode: data.auditor.FactoryCode,
            accountType: data.auditor.accountType,
            plan: data.auditor.plan
          };
          storageKey = 'auditor';
          break;
        case 'duser':
          if (!data.dUser) {
            throw new Error("Invalid response from server");
          }
          userData = {
            id: data.dUser.id,
            firstName: data.dUser.firstName,
            lastName: data.dUser.lastName,
            email: data.dUser.emailAddress,
            role: data.dUser.role,
            department: data.dUser.departmentName,
            CompanyCode: data.dUser.CompanyCode,
            BranchCode: data.dUser.BranchCode,
            FactoryCode: data.dUser.FactoryCode,
            accountType: data.dUser.accountType,
            plan: data.dUser.plan
          };
          storageKey = 'dUser';
          break;
        default:
          throw new Error("Invalid role selected");
      }

      localStorage.setItem(storageKey, JSON.stringify(userData));
      setSuccessMessage("Login successful!");
      handleRoleBasedNavigation(userData, formData.role);

    } catch (error) {
      setErrorMessage(error.message || "Failed to login. Please try again.");
    } finally {
      setLoading(false);
    } 
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
      <Row className="w-100 justify-content-center">
        <Col md={5}>
          <Card className="shadow-lg border-0 rounded-lg mb-4">
            <Card.Header className="bg-primary text-white text-center py-3">
              <h3 className="mb-0">Welcome Back</h3>
              <p className="text-white-50 mb-0">Sign in to your account</p>
            </Card.Header>

            <div className="login-form p-4">
              {errorMessage && (
                <Alert 
                  variant="danger" 
                  className="mb-4"
                  dismissible
                  onClose={() => setErrorMessage('')}
                >
                  {errorMessage}
                </Alert>
              )}
              {successMessage && (
                <Alert 
                  variant="success" 
                  className="mb-4"
                  dismissible
                  onClose={() => setSuccessMessage('')}
                >
                  {successMessage}
                </Alert>
              )}

              <Form onSubmit={handleSubmit} noValidate>
                <Form.Group className="mb-3">
                  <Form.Label>Select Role</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaUserShield /></InputGroup.Text>
                    <Form.Select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      isInvalid={!!errors.role}
                    >
                      {roleOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.role}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      isInvalid={!!errors.emailAddress}
                      placeholder="Enter email address"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.emailAddress}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Password</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaLock /></InputGroup.Text>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      isInvalid={!!errors.password}
                      placeholder="Enter password"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={togglePasswordVisibility}
                      type="button"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                <div className="d-flex justify-content-end mb-3">
                  <Link 
                    to={getForgotPasswordLink()} 
                    className="text-decoration-none text-primary"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <div className="d-grid gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                    className="py-2"
                  >
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Col>
      </Row>

      <TrialExpiredModal 
        show={showTrialModal}
        onHide={() => setShowTrialModal(false)}
        onUpgrade={() => navigate('/pricing')}
      />

      <BranchLicenseModal 
        show={showLicenseModal}
        onHide={() => setShowLicenseModal(false)}
      />
    </Container>
  );
}

export default LoginTabs;